import {
    Box,
    Button,
    Container,
    Stack,
    StackDivider,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Text,
    useToast,
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../hooks/redux';
import { LazyLoadImage } from 'react-lazy-load-image-component';

import '../../../styles/shine.css';

import Colors from '../../../config/colors';
import { FaChevronRight } from 'react-icons/fa';
import {
    useCreateCryptomusReplenishmentInvoiceMutation,
    useGetCurrenciesQuery,
    useGetRateQuery,
} from '../../../redux/platimarket/platimarket.api';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTelegram } from '../../../hooks/useTelegram';

export default function CryptomusSteamConfirmPage() {
    const {
        login,
        currency: currencyValue,
        amount,
    } = useAppSelector((state) => state.replenishment.steam);

    const { data: currenciesData } = useGetCurrenciesQuery();

    const { user, onClose } = useTelegram();
    const [t] = useTranslation();
    const toast = useToast();

    const currencies = currenciesData?.data ?? [];

    const currency = currencies.find((item) =>
        item.currency_pair.split(':')[1].includes(currencyValue),
    );

    const closePrice = currencyValue === 'USD' ? 1 : currency?.close_price!;

    const comissionPercent = +process.env.REACT_APP_COMISSION_PERCENT!;

    const comission = (amount / 100) * comissionPercent + closePrice;

    const totalAmount = amount + comission;

    const [
        fetchInvoice,
        {
            data: invoice,
            isSuccess: isSuccessInvoice,
            isError: isErrorInvoice,
            error: errorInvoice,
        },
    ] = useCreateCryptomusReplenishmentInvoiceMutation();

    const createInvoice = () => {
        if (login && user?.username) {
            fetchInvoice({ amount, currency: currencyValue, steamUsername: login, username: user.username });
        }
    };

    useEffect(() => {
        if (isSuccessInvoice && invoice) {
            const paymentURL = invoice.paymentURL;

            window.location.href = paymentURL;
        }
    }, [isSuccessInvoice]);

    useEffect(() => {
        if (isErrorInvoice) {
            toast({
                description: t('please-try-again-later'),
                status: 'error',
                duration: 10000,
            });
        }
    }, [isErrorInvoice]);

    return (
        <Container>
            <Box display="flex" flexDirection="column" gap="4">
                <Box display="flex" gap="4" alignItems="center" borderRadius="10px">
                    <LazyLoadImage
                        effect="blur"
                        src={process.env.PUBLIC_URL + '/images/replenishment/steamSquare.jpeg'}
                        style={{ height: '100px', borderRadius: '10px' }}
                    />
                    <Stat>
                        <StatNumber color="#fff" fontWeight="300">
                            {amount} {currencyValue}
                        </StatNumber>
                        <StatHelpText opacity="1" color={Colors.lightGrey2} fontWeight="300">
                            {t('replenishment-steam-account-balance')}
                        </StatHelpText>
                        <StatLabel color={Colors.lightBlue}>Steam, Valve Corporation</StatLabel>
                    </Stat>
                </Box>
                <Box fontWeight="500 " borderRadius="10px">
                    <Stack
                        divider={<StackDivider borderColor="#485765" />}
                        spacing="4"
                        fontWeight="300">
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Text color={Colors.lightGrey2}>{t('steam-login')}</Text>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap="2"
                                opacity="0.8"
                                as={Link}
                                to={'/tonkeeper/steam/login'}
                                color="#fff">
                                <Text>{login}</Text>
                                <FaChevronRight fontSize="16px" />
                            </Box>
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Text color={Colors.lightGrey2}>{t('steam-currency-and-rate')}</Text>

                            <Box
                                display="flex"
                                alignItems="center"
                                gap="2"
                                opacity="0.8"
                                color="#fff">
                                <Text>
                                    {currencyValue}{' '}
                                    {currencyValue != 'USD' && currency?.close_price
                                        ? '/ ' + closePrice
                                        : ''}
                                </Text>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Text color={Colors.lightGrey2}>{t('comission')}</Text>
                            <Text opacity="0.8" color="#fff">
                                {comission.toFixed(2)} {currencyValue}
                            </Text>
                        </Box>

                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Text color={Colors.lightGrey2}>{t('total')}</Text>
                            <Text opacity="0.8" color={Colors.lightBlue}>
                                {totalAmount.toFixed(2)} {currencyValue}
                            </Text>
                        </Box>
                    </Stack>
                </Box>
                <Box
                    position="fixed"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    bottom="0"
                    zIndex="3"
                    left="0"
                    right="0"
                    padding="20px"
                    flexDirection="column"
                    gap="3">
                    <Button
                        onClick={() => createInvoice()}
                        className="shine"
                        variant="solid"
                        color="white"
                        background={Colors.blue}
                        fontSize="16px"
                        fontWeight="500"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap="12px"
                        width="100%"
                        padding="25px 0px"
                        borderRadius="8px"
                        backgroundImage={
                            process.env.PUBLIC_URL + '/images/categories/background_spotlight.jpg'
                        }
                        backgroundSize="cover"
                        backgroundPosition="bottom">
                        <Text>
                            {t('pay')} {totalAmount.toFixed(2)} {currencyValue}
                        </Text>
                    </Button>
                    <Text
                        fontSize="12px"
                        textAlign="center"
                        color={Colors.lightGrey2}
                        fontWeight="300">
                        {t('pay-description')}
                    </Text>
                </Box>
            </Box>
        </Container>
    );
}
